<template>
	<V2Layout>
		<div class="v2-cmp">

			<!-- Header -->
			<div class="v2-cmp__header">
				<div class="v2-cmp__header-left">
					<svg class="v2-cmp__header-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
						<rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
						<rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
						<rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
					</svg>
					<h1 class="v2-cmp__title">Campaigns</h1>
					<span class="v2-cmp__count">{{ totalCount }} total</span>
				</div>
			</div>

			<!-- Filter Bar -->
			<div class="v2-cmp__filters">
				<div class="v2-cmp__pills">
					<button
						v-for="f in statusFilters"
						:key="f.value"
						class="v2-cmp__pill"
						:class="{ 'v2-cmp__pill--active': activeStatus === f.value }"
						@click="setStatus(f.value)"
					>
						{{ f.label }}
						<span v-if="f.count != null" class="v2-cmp__pill-count">{{ f.count }}</span>
					</button>
				</div>

				<div class="v2-cmp__filter-right">
					<div class="v2-cmp__search">
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="6" cy="6" r="4.5" stroke="currentColor" stroke-width="1.3"/><path d="M9.5 9.5L13 13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
						<input
							v-model="searchQuery"
							type="text"
							placeholder="Search campaigns..."
							class="v2-cmp__search-input"
							@input="onSearch"
						/>
					</div>

					<div class="v2-cmp__view-toggle">
						<button
							class="v2-cmp__view-btn"
							:class="{ 'v2-cmp__view-btn--active': viewMode === 'cards' }"
							@click="viewMode = 'cards'"
							title="Card view"
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.3"/><rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.3"/></svg>
						</button>
						<button
							class="v2-cmp__view-btn"
							:class="{ 'v2-cmp__view-btn--active': viewMode === 'table' }"
							@click="viewMode = 'table'"
							title="Table view"
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4H14M2 8H14M2 12H14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
						</button>
					</div>
				</div>
			</div>

			<!-- Card View -->
			<div v-if="viewMode === 'cards'" class="v2-cmp__cards">
				<div
					v-for="campaign in campaigns"
					:key="campaign.id"
					class="v2-cmp-card"
					@click="openCampaign(campaign.id)"
				>
					<div class="v2-cmp-card__top">
						<div class="v2-cmp-card__name-block">
							<span class="v2-cmp-card__name">{{ campaign.name }}</span>
						</div>
						<span class="v2-cmp-card__status" :class="statusBadgeClass(campaign)">{{ statusLabel(campaign) }}</span>
					</div>

					<div class="v2-cmp-card__info">
						<!-- Budget -->
						<div class="v2-cmp-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1.1"/><path d="M6 3V9M4 5H8M4.5 7H7.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>
							<span v-if="!isSSPViewer">{{ formatCashValue(campaign.spent) }} / {{ formatCashValue(campaign.budget) }} {{ currencyLabel(campaign) }}</span>
							<span v-else>{{ formatCashValue(campaign.budget) }} {{ currencyLabel(campaign) }}</span>
						</div>

						<!-- Impressions / Ad Plays -->
						<div class="v2-cmp-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 6C1 6 3 2 6 2C9 2 11 6 11 6C11 6 9 10 6 10C3 10 1 6 1 6Z" stroke="currentColor" stroke-width="1.1"/><circle cx="6" cy="6" r="1.5" stroke="currentColor" stroke-width="1.1"/></svg>
							<span v-if="campaign.payment_model_counted">{{ Math.floor(campaign.payment_model_counted) }}</span>
							<span v-else>&mdash;</span>
						</div>

						<!-- Devices -->
						<div class="v2-cmp-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="1" y="2" width="10" height="7" rx="1" stroke="currentColor" stroke-width="1.1"/><path d="M4 11H8" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/><path d="M6 9V11" stroke="currentColor" stroke-width="1.1"/></svg>
							{{ campaign.device_count != null ? campaign.device_count : '—' }}
						</div>

						<!-- Date Range -->
						<div class="v2-cmp-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="1" y="2" width="10" height="9" rx="1" stroke="currentColor" stroke-width="1.1"/><path d="M1 5H11" stroke="currentColor" stroke-width="1.1"/><path d="M4 1V3M8 1V3" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>
							{{ formatDate(campaign.start_date) }} — {{ formatDate(campaign.end_date) }}
						</div>
					</div>

					<div class="v2-cmp-card__footer">
						<span v-if="isSSP" class="v2-cmp-card__agency">{{ ownerName(campaign) }}</span>
						<span v-else class="v2-cmp-card__agency"></span>
						<span class="v2-cmp-card__date">{{ formatDateTime(campaign.ctime) }}</span>
					</div>
				</div>

				<div v-if="!campaigns.length" class="v2-cmp__empty">
					<p>No campaigns found</p>
					<p class="v2-cmp__empty-sub">Try adjusting your search or filters</p>
				</div>
			</div>

			<!-- Table View -->
			<div v-else class="v2-cmp__table-wrap">
				<table class="nice-table">
					<thead>
						<tr>
							<th>ID&nbsp;&mdash;</th>
							<th>Name&nbsp;&mdash;</th>
							<th v-if="isSSP">DSP Agency&nbsp;&mdash;</th>
							<th>Payment&nbsp;Model&nbsp;&mdash;</th>
							<th>Status&nbsp;&mdash;</th>
							<th>Budget&nbsp;&mdash;</th>
							<th>Impressions&nbsp;&mdash;</th>
							<th>Devices&nbsp;&mdash;</th>
							<th>Start&nbsp;&mdash;</th>
							<th>End&nbsp;&mdash;</th>
							<th>Created&nbsp;&mdash;</th>
							<th>Updated&nbsp;&mdash;</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="campaign in campaigns"
							:key="campaign.id"
							class="v2-cmp__row"
							@click="openCampaign(campaign.id)"
						>
							<td>{{ campaign.id }}</td>
							<td class="v2-cmp__row-name">{{ campaign.name }}</td>
							<td v-if="isSSP">{{ ownerName(campaign) }}</td>
							<td>{{ campaign.payment_model }}</td>
							<td><span class="v2-cmp-card__status" :class="statusBadgeClass(campaign)">{{ statusLabel(campaign) }}</span></td>
							<td>
								<span v-if="!isSSPViewer" v-html="formatCashSpent(campaign.spent, campaign.budget, campaign.currency)"></span>
								<span v-else v-html="formatCash(campaign.budget, campaign.currency)"></span>
							</td>
							<td>
								<span v-if="campaign.payment_model_counted">{{ Math.floor(campaign.payment_model_counted) }}</span>
								<span v-else>&mdash;</span>
							</td>
							<td>{{ campaign.device_count != null ? campaign.device_count : '—' }}</td>
							<td>{{ formatDate(campaign.start_date) }}</td>
							<td>{{ formatDate(campaign.end_date) }}</td>
							<td>{{ formatDateTime(campaign.ctime) }}</td>
							<td>{{ formatDateTime(campaign.mtime) }}</td>
						</tr>
						<tr v-if="!campaigns.length">
							<td :colspan="isSSP ? 11 : 10" class="v2-cmp__empty-cell">No campaigns found</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Pagination -->
			<div class="v2-cmp__pagination">
				<span class="v2-cmp__pagination-info">Showing {{ paginationFrom }}–{{ paginationTo }} of {{ totalCount }}</span>
				<div class="v2-cmp__pagination-right">
					<span class="v2-cmp__pagination-label">Lines per page</span>
					<select v-model.number="pageSize" class="v2-cmp__pagination-select" @change="loadPage(1)">
						<option :value="20">20</option>
						<option :value="50">50</option>
						<option :value="100">100</option>
					</select>
					<div class="v2-cmp__pagination-pages">
						<button class="v2-cmp__page-btn" :disabled="page <= 1" @click="loadPage(page - 1)">&lt;</button>
						<button v-for="p in visiblePages" :key="p" class="v2-cmp__page-btn" :class="{ 'v2-cmp__page-btn--active': p === page }" @click="loadPage(p)">{{ p }}</button>
						<button class="v2-cmp__page-btn" :disabled="page >= totalPages" @click="loadPage(page + 1)">&gt;</button>
					</div>
				</div>
			</div>

			<router-view />
		</div>
	</V2Layout>
</template>


<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import V2Layout from '@/layouts/V2Layout';
import {
	formatCash,
	formatCashSpent,
	profileFormatDate as formatDate,
	profileFormatDateTime as formatDateTime,
} from '@/utilites';
import {
	APPROVED as STATUS_PLAYING,
	PENDING_AUDIT as STATUS_AWAITING_APPROVAL,
	DENIED as STATUS_REJECTED,
	PAUSED as STATUS_PAUSED,
	FINISHED as STATUS_FINISHED,
	ARCHIVED as STATUS_ARCHIVED,
	PENDING_AUDIT_EXTENDED_APPROVED,
	PARTIALLY_APPROVED_EXTENDED_APPROVED,
} from '@/constants/audit-status-codes';
import { getExtendedStatus } from '@/constants/utils';

export default {
	name: 'V2CampaignList',
	components: { V2Layout },

	data() {
		return {
			page: 1,
			pageSize: 20,
			searchQuery: '',
			activeStatus: '---',
			viewMode: localStorage.getItem('v2CampaignViewMode') || 'cards',
			searchTimeout: null,
		};
	},

	async mounted() {
		await this.requestCampaigns();
	},

	watch: {
		viewMode(val) { localStorage.setItem('v2CampaignViewMode', val); },

		appAgency(value, oldValue) {
			if (value && value !== oldValue) {
				this.requestCampaigns();
			}
		},
	},

	computed: {
		...mapState('app', {
			appAgency: 'agency',
			appSide: 'side',
		}),
		...mapGetters('app', [
			'isSSPAdminOrApprover',
			'isSSPViewer',
			'isDSP',
		]),

		...mapState('campaign', {
			campaignList: 'list',
			campaignTotalCount: 'totalCount',
		}),

		isSSP() {
			return this.appSide === 'ssp';
		},

		campaigns() {
			return this.campaignList || [];
		},

		totalCount() {
			return this.campaignTotalCount || 0;
		},

		statusFilters() {
			return [
				{ value: '---', label: 'All', count: this.totalCount },
				{ value: STATUS_PLAYING, label: 'Playing', count: null },
				{ value: STATUS_PAUSED, label: 'Paused', count: null },
				{ value: STATUS_AWAITING_APPROVAL, label: 'Awaiting', count: null },
				{ value: STATUS_FINISHED, label: 'Finished', count: null },
				{ value: STATUS_REJECTED, label: 'Rejected', count: null },
			];
		},

		totalPages() { return Math.ceil(this.totalCount / this.pageSize) || 1; },
		paginationFrom() { return this.totalCount ? (this.page - 1) * this.pageSize + 1 : 0; },
		paginationTo() { return Math.min(this.page * this.pageSize, this.totalCount); },

		visiblePages() {
			const pages = [];
			for (let i = Math.max(1, this.page - 2); i <= Math.min(this.totalPages, this.page + 2); i++) pages.push(i);
			return pages;
		},
	},

	methods: {
		...mapActions('campaign', {
			'requestCampaignList': 'requestList',
			'requestCampaignStatList': 'requestStatList',
		}),

		formatCash,
		formatCashSpent,
		formatDate,
		formatDateTime,

		async requestCampaigns() {
			let loading = this.$loading.show();
			try {
				const filters = {};
				if (this.searchQuery) filters.search = this.searchQuery;
				if (this.activeStatus !== '---') filters.status = this.activeStatus;

				await this.requestCampaignList({
					filters,
					page: this.page,
					pageSize: this.pageSize,
					force: true,
				});

				// Load stats (spent, device_count, payment_model_counted)
				await this.requestCampaignStatList({
					filters,
					page: this.page,
					pageSize: this.pageSize,
				});
			} catch (err) {
				this.$log.error(err);
			} finally {
				loading.hide();
			}
		},

		setStatus(s) {
			this.activeStatus = s;
			this.page = 1;
			this.requestCampaigns();
		},

		onSearch() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.page = 1;
				this.requestCampaigns();
			}, 300);
		},

		loadPage(p) {
			if (p >= 1 && p <= this.totalPages) {
				this.page = p;
				this.requestCampaigns();
			}
		},

		openCampaign(id) {
			this.$router.push({ name: 'v2-campaign-details', params: { id } });
		},

		campaignStatus(campaign) {
			if (!campaign.status || campaign.status.length === 0) return null;
			return getExtendedStatus(campaign);
		},

		statusLabel(campaign) {
			const s = this.campaignStatus(campaign);
			if (s === STATUS_PLAYING) return 'Playing';
			if (s === PARTIALLY_APPROVED_EXTENDED_APPROVED) return 'Partially Playing';
			if (s === PENDING_AUDIT_EXTENDED_APPROVED) return 'Awaiting (Playing)';
			if (s === STATUS_PAUSED) return 'Paused';
			if (s === STATUS_FINISHED) return 'Finished';
			if (s === STATUS_AWAITING_APPROVAL) return 'Awaiting';
			if (s === STATUS_REJECTED) return 'Rejected';
			if (s === STATUS_ARCHIVED) return 'Archived';
			return '';
		},

		statusBadgeClass(campaign) {
			const s = this.campaignStatus(campaign);
			if (s === STATUS_PLAYING) return 'v2-cmp-card__status--playing';
			if (s === PARTIALLY_APPROVED_EXTENDED_APPROVED) return 'v2-cmp-card__status--playing';
			if (s === PENDING_AUDIT_EXTENDED_APPROVED) return 'v2-cmp-card__status--awaiting';
			if (s === STATUS_PAUSED) return 'v2-cmp-card__status--paused';
			if (s === STATUS_FINISHED) return 'v2-cmp-card__status--finished';
			if (s === STATUS_AWAITING_APPROVAL) return 'v2-cmp-card__status--awaiting';
			if (s === STATUS_REJECTED) return 'v2-cmp-card__status--rejected';
			if (s === STATUS_ARCHIVED) return 'v2-cmp-card__status--archived';
			return '';
		},

		ownerName(campaign) {
			return campaign.owner_instance ? campaign.owner_instance.name : '';
		},

		currencyLabel(campaign) {
			return campaign.currency || '';
		},

		formatCashValue(value) {
			const num = parseFloat(value);
			if (isNaN(num)) return value;
			return parseFloat(num.toFixed(2));
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

.v2-cmp
	font-family: $v2-font

.v2-cmp__header
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 20px

.v2-cmp__header-left
	display: flex
	align-items: center
	gap: 10px

.v2-cmp__header-icon
	color: $v2-text-muted

.v2-cmp__title
	font-size: 22px
	font-weight: 700
	margin: 0
	color: $v2-text

.v2-cmp__count
	font-size: 12px
	color: $v2-text-muted
	background: #f3f4f6
	padding: 3px 10px
	border-radius: 20px

.v2-cmp__filters
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 20px
	gap: 16px
	flex-wrap: wrap

.v2-cmp__pills
	display: flex
	gap: 6px

.v2-cmp__pill
	display: inline-flex
	align-items: center
	gap: 4px
	padding: 6px 14px
	border: 1.5px solid $v2-border
	border-radius: 20px
	background: transparent
	font-family: $v2-font
	font-size: 12px
	font-weight: 500
	color: $v2-text-muted
	cursor: pointer
	transition: all 0.15s

	&:hover
		border-color: $v2-teal
		color: $v2-teal

	&--active
		background: rgba($v2-teal, 0.08)
		border-color: $v2-teal
		color: $v2-teal
		font-weight: 600

.v2-cmp__pill-count
	font-size: 11px
	opacity: 0.7

.v2-cmp__filter-right
	display: flex
	align-items: center
	gap: 10px

.v2-cmp__search
	display: flex
	align-items: center
	gap: 8px
	padding: 7px 14px
	background: $v2-card-bg
	border: 1px solid $v2-border
	border-radius: 10px
	min-width: 220px
	color: $v2-text-muted

.v2-cmp__search-input
	border: none !important
	outline: none !important
	background: transparent !important
	font-family: $v2-font
	font-size: 13px
	color: $v2-text
	width: 100%
	padding: 0 !important
	min-height: 0 !important
	box-shadow: none !important

	&::placeholder
		color: $v2-text-label

.v2-cmp__view-toggle
	display: flex
	background: #f3f4f6
	border-radius: 8px
	padding: 2px

.v2-cmp__view-btn
	display: flex
	align-items: center
	justify-content: center
	width: 32px
	height: 32px
	border: none
	background: transparent
	border-radius: 6px
	cursor: pointer
	color: $v2-text-muted
	transition: all 0.15s

	&:hover
		color: $v2-text

	&--active
		background: $v2-card-bg
		color: $v2-text
		box-shadow: 0 1px 3px rgba(0,0,0,0.08)

.v2-cmp__cards
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 14px

	@media (max-width: 1024px)
		grid-template-columns: repeat(2, 1fr)

	@media (max-width: 768px)
		grid-template-columns: 1fr

.v2-cmp-card
	background: $v2-card-bg
	border: 1px solid $v2-border
	border-radius: $v2-radius-card
	padding: 18px 20px
	cursor: pointer
	transition: all 0.2s
	display: flex
	flex-direction: column

	&:hover
		box-shadow: $v2-shadow-hover
		transform: translateY(-2px)

.v2-cmp-card__top
	display: flex
	align-items: flex-start
	justify-content: space-between
	gap: 10px
	margin-bottom: 14px

.v2-cmp-card__name-block
	flex: 1
	min-width: 0

.v2-cmp-card__name
	font-size: 14px
	font-weight: 600
	color: $v2-text
	display: block

.v2-cmp-card__status
	font-size: 11px
	font-weight: 600
	padding: 3px 10px
	border-radius: 20px
	white-space: nowrap
	flex-shrink: 0

	&--playing
		background: rgba($v2-green, 0.1)
		color: darken($v2-green, 10%)

	&--paused
		background: rgba($v2-purple, 0.1)
		color: $v2-purple

	&--awaiting
		background: rgba(#eab308, 0.1)
		color: #a16207

	&--finished
		background: rgba($v2-blue, 0.1)
		color: $v2-blue

	&--rejected
		background: rgba($v2-red, 0.1)
		color: $v2-red

	&--archived
		background: rgba(#9ca3af, 0.1)
		color: #6b7280

.v2-cmp-card__info
	display: flex
	flex-direction: column
	gap: 6px
	margin-bottom: 14px
	flex: 1

.v2-cmp-card__row
	display: flex
	align-items: center
	gap: 8px
	font-size: 12px
	color: $v2-text-muted

	svg
		flex-shrink: 0
		color: $v2-text-label

.v2-cmp-card__footer
	display: flex
	justify-content: space-between
	align-items: center
	padding-top: 12px
	border-top: 1px solid $v2-border

.v2-cmp-card__agency
	font-size: 11px
	color: $v2-text-muted

.v2-cmp-card__date
	font-size: 11px
	color: $v2-text-label

.v2-cmp__table-wrap
	overflow-x: auto

.v2-cmp__row
	cursor: pointer

.v2-cmp__row-name
	font-weight: 600

.v2-cmp__empty-cell
	text-align: center
	padding: 40px !important
	color: $v2-text-muted

.v2-cmp__empty
	grid-column: 1 / -1
	text-align: center
	padding: 60px 20px
	color: $v2-text-muted

.v2-cmp__empty-sub
	font-size: 13px
	color: $v2-text-label

.v2-cmp__pagination
	display: flex
	align-items: center
	justify-content: space-between
	margin-top: 20px

.v2-cmp__pagination-info
	font-size: 13px
	color: $v2-text-muted

.v2-cmp__pagination-right
	display: flex
	align-items: center
	gap: 12px

.v2-cmp__pagination-label
	font-size: 12px
	color: $v2-text-muted

.v2-cmp__pagination-select
	padding: 5px 8px
	border: 1px solid $v2-border
	border-radius: 8px
	font-family: $v2-font
	font-size: 12px
	background: $v2-card-bg
	color: $v2-text

.v2-cmp__pagination-pages
	display: flex
	gap: 4px

.v2-cmp__page-btn
	width: 32px
	height: 32px
	display: flex
	align-items: center
	justify-content: center
	border: 1px solid $v2-border
	border-radius: 8px
	background: $v2-card-bg
	font-family: $v2-font
	font-size: 12px
	font-weight: 500
	color: $v2-text-muted
	cursor: pointer
	transition: all 0.15s

	&:hover:not(:disabled)
		border-color: $v2-teal
		color: $v2-teal

	&:disabled
		opacity: 0.4
		cursor: default

	&--active
		background: $v2-teal
		border-color: $v2-teal
		color: #fff
</style>
